/* eslint-disable camelcase */
// Presentation helpers shared by every report-schedule surface.
//
// The API keeps its machine-readable formats (ISO dates, `previous_month`,
// `HH:mm:ss`); everything here formats those for display only. British order
// throughout, because the product is British and the reporting header already
// renders DD/MM/YYYY — a page that mixed the two would be worse than either.

// Entry format for the pickers. Display formatting below is the longer,
// unambiguous form; this constant is only for fields the user types into.
export const UK_DATE_FORMAT = 'DD/MM/YYYY';

// Shown where a value is genuinely absent. Every column on the unified table
// applies to both kinds, so there is no "inapplicable" state to render: a CRM
// schedule has one location, one output format and the London zone the legacy
// scheduler is fixed to, just as a client report has several locations and
// its own zone.
export const NOT_AVAILABLE = 'Not available';

// The name the backend substitutes for a location the viewer cannot reach.
// Shown, but never treated as a real practice: `id` is what decides that.
export const UNAVAILABLE_LOCATION = 'Unavailable location';

const UK_DATE_PARTS = { day: '2-digit', month: 'short', year: 'numeric' };
const UK_TIME_PARTS = { hour: '2-digit', minute: '2-digit', hour12: false };

const toDate = (value) => {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

// Intl throws on an unknown IANA zone, and a saved schedule can carry one the
// browser does not know. Fall back to the viewer's own zone rather than
// crashing a whole table row.
const safeFormat = (date, parts, timeZone) => {
    if (!date) return NOT_AVAILABLE;
    try {
        return new Intl.DateTimeFormat('en-GB', timeZone ? { ...parts, timeZone } : parts).format(date);
    } catch {
        return new Intl.DateTimeFormat('en-GB', parts).format(date);
    }
};

export const formatUkDate = (value, timeZone = undefined) => safeFormat(toDate(value), UK_DATE_PARTS, timeZone);

export const formatUkDateTime = (value, timeZone = undefined) => {
    const date = toDate(value);
    if (!date) return NOT_AVAILABLE;
    return safeFormat(date, { ...UK_DATE_PARTS, ...UK_TIME_PARTS }, timeZone);
};

// `09:00:00` / `09:00` -> `09:00`. Left as a string on purpose: a bare
// time-of-day has no date, so putting it through a Date only invents one.
export const formatTimeOfDay = (value) => {
    if (typeof value !== 'string') return NOT_AVAILABLE;
    const match = value.match(/^(\d{1,2}):(\d{2})/);
    if (!match) return NOT_AVAILABLE;
    return `${match[1].padStart(2, '0')}:${match[2]}`;
};

// The API sends `period` as a machine value and `period_display` as the
// label, so there is no map to keep in step here. This is the fallback for a
// row that somehow arrives without the label.
export const PERIOD_LABELS = {
    previous_month: 'Previous month',
    previous_three_months: 'Previous 3 months',
    DAILY: 'Daily',
    WEEKLY: 'Weekly',
    MONTHLY: 'Monthly',
    YEARLY: 'Yearly',
};

export const formatPeriod = (period, periodDisplay) =>
    periodDisplay || PERIOD_LABELS[period] || period || NOT_AVAILABLE;

export const FORMAT_LABELS = {
    PDF: 'PDF',
    EXCEL: 'Excel',
    CSV: 'CSV',
    HTML_INLINE: 'Email body',
};

export const formatArtifactFormats = (formats) => {
    const list = (Array.isArray(formats) ? formats : []).filter(Boolean);
    if (!list.length) return NOT_AVAILABLE;
    return list.map((value) => FORMAT_LABELS[value] || value).join(', ');
};

// Locations arrive as `{id, name}` objects in saved order. `id` is non-null
// exactly when the viewer can reach that practice; an entry they cannot reach
// keeps its position with a null id and a placeholder name, so the list
// length still equals the number of locations the schedule covers.
//
// Reachability is read from the id, not from the placeholder name: a real
// practice could in principle be named anything, and a name comparison would
// be the kind of check that silently stops working.
export function parseLocations(locations) {
    const entries = (Array.isArray(locations) ? locations : [])
        .map((entry) => {
            if (typeof entry === 'string') {
                // Tolerated for the earlier string-only shape. Without an id
                // there is nothing to link to, so treat it as reachable text.
                const name = entry.trim();
                return name ? { id: null, name, reachable: name !== UNAVAILABLE_LOCATION } : null;
            }
            if (entry && typeof entry === 'object') {
                const name = String(entry.name || '').trim();
                if (!name) return null;
                return { id: entry.id ?? null, name, reachable: entry.id !== null && entry.id !== undefined };
            }
            return null;
        })
        .filter(Boolean);
    return {
        entries,
        names: entries.map((entry) => entry.name),
        unavailable: entries.filter((entry) => !entry.reachable).length,
    };
}

export function formatLocations(locations) {
    const { names } = parseLocations(locations);
    if (!names.length) return NOT_AVAILABLE;
    return names.join(', ');
}
